/**
 * Decide which source attribute each shipped CDT tier reads. The four tiers are a
 * contract about blocking (cdt0 x-zone, cdt1 column, cdt2 shelf band, cdt3 within-shelf
 * sort), so the assignment is measured off the merchant's base pog (`blockOrientation`),
 * never chosen by cardinality. A measured-vertical attribute never lands on a banded
 * tier; an ambiguous one is reported for a ruling, not defaulted; the output is
 * deterministic. Rules in ALGORITHMS.md.
 */
import {
  type BandednessReading,
  type BlockRole,
  type BlockingReading,
  type BlockingScope,
  VERTICAL_SCORE,
  measureBandednessOf,
  measureBlockingOf,
  rootBlockingScopes,
  splitBlockingScopes,
} from "./blockOrientation";
import {
  type Orientation,
  type SpineLevel,
  type StudioDraft,
  type StudioItem,
  type StudioRunGeometry,
  effectiveAttrs,
  isPlaceholderValue,
  levelsForScope,
} from "./studioTypes";

/** The shipped tiers. Not a tunable: `buildFromCdt` and pogger both assume four. */
export const SHIPPED_LEVELS = 4;
/** Vertical tiers. Also not a tunable — it is the solver's `verticalDepth`. */
export const VERTICAL_TIERS = 2;
/** Above this share of one-SKU cdt2 nodes the level carries identity, not grouping. */
export const CDT2_SINGLETON_BUDGET = 1 / 3;
/** SKUs per node the sort tier must still average; below it the tier names items. */
export const SORT_TIER_MIN_OCCUPANCY = 1.5;

export type LevelRole = "vertical" | "horizontal";

export interface PlannedLevel {
  levels: SpineLevel[];
  /** The engine's geometric sense (see `orientOfTier`), derived from the tier, never
   *  inherited from the source level. */
  orient: Orientation;
  role: LevelRole;
  /** Why this attribute won this tier, in one line, for the report panel. */
  because: string;
}

/** Internal `orient` is the geometric complement of the merchandising word: "horizontal"
 *  = children separate along x = vertical blocking. cdt2 alone stacks shelf bands. */
const orientOfTier = (tier: number): Orientation => (tier === 2 ? "vertical" : "horizontal");

export interface CdtLevelPlanOptions {
  /** Human rulings for ambiguous attributes, keyed by attribute ("form") or by branch
   *  ("Detergent / form"); the branch-scoped entry wins. */
  rulings?: Readonly<Record<string, LevelRole>>;
  /** The attribute a human asserts for a tier, whatever the wall measured. An
   *  assertion the plan cannot honour is reported, never silently dropped. */
  assign?: Readonly<{ cdt0?: string; cdt1?: string; cdt2?: string }>;
  singletonBudget?: number;
}

/** One cdt0 branch's own tiers: cdt1..cdt3 are decided per branch, only cdt0 is shared. */
export interface BranchPlan {
  /** The cdt0 value that names this branch. */
  value: string;
  skus: number;
  /** Source attributes each tier reads here, indexed by tier. */
  tiers: readonly (readonly string[])[];
  because: readonly string[];
}

export interface CdtLevelPlan {
  levels: PlannedLevel[];
  branches: readonly BranchPlan[];
  verticality: readonly BlockingReading[];
  bandedness: readonly BandednessReading[];
  /** Ambiguous attributes with no recorded ruling. Each is a question, not a bug. */
  needsRuling: readonly BlockingReading[];
  /** Vertical attributes that lost a tier — there are only two. */
  demoted: readonly string[];
  /** One value plan-wide: they split nothing and must not spend a tier. */
  inert: readonly string[];
  /** Set when the measured tiers do not follow the working spine's order: the tree
   *  re-nests rather than coarsens, and `collapse4` reports the diffs. */
  reordered?: { before: readonly string[]; after: readonly string[] };
  warnings: readonly string[];
}

const distinctValues = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  attr: string,
): Set<string> => {
  const values = new Set<string>();
  for (const item of items) {
    const value = String(effectiveAttrs(item, draft)[attr] ?? "");
    if (!isPlaceholderValue(value)) values.add(value);
  }
  return values;
};

/** Items carrying the draft's overrides, so the measurement sees retagged values. */
const resolvedItems = (items: readonly StudioItem[], draft: StudioDraft): StudioItem[] =>
  items.map((item) => ({ ...item, attrs: effectiveAttrs(item, draft) }));

/** SKUs per node of a compound tier; at 1 the tier names items, not groups. */
const nodeOccupancy = (scopes: readonly BlockingScope[], attrs: readonly string[]): number => {
  const counts = new Map<string, number>();
  let total = 0;
  for (const scope of scopes) {
    for (const item of scope.items) {
      const key = [
        scope.path.join(" "),
        ...attrs.map((attr) => String(item.attrs[attr] ?? "")),
      ].join(" | ");
      counts.set(key, (counts.get(key) ?? 0) + 1);
      total += 1;
    }
  }
  return counts.size === 0 ? 0 : total / counts.size;
};

const singletonShare = (
  items: readonly StudioItem[],
  scopes: readonly BlockingScope[],
  attr: string,
): number => {
  const counts = new Map<string, number>();
  for (const scope of scopes) {
    for (const item of scope.items) {
      const key = `${scope.path.join(" ")} ${String(item.attrs[attr] ?? "")}`;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
  }
  if (counts.size === 0) return 0;
  let singles = 0;
  for (const count of counts.values()) if (count === 1) singles += 1;
  return singles / counts.size;
};

/** Plan the four tiers from the base pog. `items` must carry the merchant's placements:
 *  measuring a layout this pipeline generated feeds the classifier its own conclusion. */
export const planCdtLevels = (
  items: readonly StudioItem[],
  draft: StudioDraft,
  runs: readonly StudioRunGeometry[],
  options: CdtLevelPlanOptions = {},
): CdtLevelPlan => {
  const budget = options.singletonBudget ?? CDT2_SINGLETON_BUDGET;
  const rulings = options.rulings ?? {};
  const warnings: string[] = [];
  const measured = resolvedItems(items, draft);

  const live: SpineLevel[] = [];
  const inert: string[] = [];
  for (const level of draft.spine) {
    if (distinctValues(items, draft, level.attr).size > 1) live.push(level);
    else inert.push(level.attr);
  }
  // A spine of nothing but constants still has to ship something addressable.
  const spine = live.length > 0 ? live : [...draft.spine];
  if (spine.length === 0) {
    warnings.push("No CDT levels can be planned because the plan carries no usable attributes.");
    return {
      levels: [],
      branches: [],
      verticality: [],
      bandedness: [],
      needsRuling: [],
      demoted: [],
      inert,
      warnings,
    };
  }
  const spineRank = new Map(spine.map((level, index) => [level.attr, index]));

  const verticality = spine.map((level) => measureBlockingOf(measured, level.attr, runs));
  const readingOf = new Map(verticality.map((reading) => [reading.attr, reading]));
  const roleOf = (attr: string): BlockRole =>
    rulings[attr] ?? readingOf.get(attr)?.role ?? "ambiguous";

  const needsRuling = verticality.filter(
    (reading) => reading.role === "ambiguous" && rulings[reading.attr] === undefined,
  );
  for (const reading of needsRuling) if (reading.warning) warnings.push(reading.warning);

  /* ---- 1. cdt0, decided over the whole plan ---- */
  const scoreOf = (attr: string) => readingOf.get(attr)?.score ?? 0;
  const byVerticality = [...spine].sort(
    (a, b) =>
      scoreOf(b.attr) - scoreOf(a.attr) ||
      (spineRank.get(a.attr) ?? 0) - (spineRank.get(b.attr) ?? 0),
  );
  const assign = options.assign ?? {};
  const asserted = (tier: "cdt0" | "cdt1" | "cdt2"): SpineLevel | undefined => {
    const attr = assign[tier];
    if (attr === undefined) return undefined;
    const level = spine.find((candidate) => candidate.attr === attr);
    if (!level) {
      warnings.push(
        `${tier} was asserted as "${attr}", which this plan does not carry — the measured order stands instead.`,
      );
      return undefined;
    }
    return level;
  };
  /* cdt0 is the draft's own root, never the measurement's pick: restriction rows name
     its values by exact string. The wall may comment on the root, not replace it. */
  const declaredLevels = levelsForScope(draft.spine, draft.scopedLevelOrder, []).filter((level) =>
    spine.includes(level),
  );
  const root = asserted("cdt0") ?? declaredLevels[0] ?? byVerticality[0];
  /** The column tier the WHOLE wall votes for — the fallback a branch has to beat. */
  const planColumn = byVerticality.find((level) => level !== root);
  if (root && roleOf(root.attr) !== "vertical") {
    warnings.push(
      `cdt0 holds "${root.attr}", which the base pog does not read as columnar (${scoreOf(root.attr).toFixed(2)}). It owns the x-zones the restriction rows name, and the wall does not agree that it does.`,
    );
  }
  if (root && byVerticality[0] && byVerticality[0].attr !== root.attr) {
    warnings.push(
      `The base pog reads "${byVerticality[0].attr}" (${scoreOf(byVerticality[0].attr).toFixed(2)}) as the coarsest columnar attribute rather than "${root.attr}" (${scoreOf(root.attr).toFixed(2)}). The tree keeps the root it was given.`,
    );
  }

  /* ---- 2. cdt1..cdt3, decided once per branch; only the ROLE of each tier is
     plan-wide (cdt1 columnar, cdt2 banded), which attribute fills it may differ. ---- */
  const branchScopes = new Map<string, BlockingScope[]>();
  for (const scope of splitBlockingScopes(rootBlockingScopes(measured, runs), root.attr)) {
    const value = scope.path[scope.path.length - 1];
    let bucket = branchScopes.get(value);
    if (!bucket) {
      bucket = [];
      branchScopes.set(value, bucket);
    }
    bucket.push(scope);
  }

  const bandTieMargin = 0.1;
  const branches: BranchPlan[] = [];
  const usedAt = new Map<number, Map<string, number>>();
  const noteUse = (tier: number, attr: string, weight: number) => {
    let bucket = usedAt.get(tier);
    if (!bucket) {
      bucket = new Map();
      usedAt.set(tier, bucket);
    }
    bucket.set(attr, (bucket.get(attr) ?? 0) + weight);
  };
  const allVerticality: BlockingReading[] = [...verticality];
  const allBandedness: BandednessReading[] = [];
  const demoted: string[] = [];

  for (const [value, scopes] of branchScopes) {
    const branchItems = scopes.flatMap((scope) => [...scope.items]);
    const weight = branchItems.length;
    // A level with one value in this branch must not spend a tier here.
    const valuesIn = (attr: string) =>
      new Set(
        branchItems
          .map((item) => String(item.attrs[attr] ?? ""))
          .filter((entry) => !isPlaceholderValue(entry)),
      );
    const ruledHere = (attr: string): LevelRole | undefined =>
      rulings[`${value} / ${attr}`] ?? rulings[attr];
    const pool = spine.filter((level) => level !== root);
    const splitting = pool.filter((level) => valuesIn(level.attr).size > 1);
    /* A branch with a stated order (`scopedLevelOrder`, written by per-node inference or
       a hierarchy edit) keeps it; measurement decides only where nothing was said. */
    const stated = draft.scopedLevelOrder?.[value]
      ? levelsForScope(draft.spine, draft.scopedLevelOrder, [value]).filter(
          (level) => level !== root && spine.includes(level) && valuesIn(level.attr).size > 1,
        )
      : undefined;
    const usable = splitting.length > 0 ? splitting : pool;

    /* cdt1: the most columnar attribute INSIDE this branch. */
    const branchVertical = usable.map((level) =>
      measureBlockingOf(measured, level.attr, runs, scopes),
    );
    for (const reading of branchVertical)
      allVerticality.push({ ...reading, attr: `${value} / ${reading.attr}` });
    const branchScoreOf = new Map(branchVertical.map((reading) => [reading.attr, reading.score]));
    const columnRuling = asserted("cdt1");
    const columnCandidates = usable.filter((level) => ruledHere(level.attr) !== "horizontal");
    const branchBest = [...columnCandidates].sort(
      (a, b) =>
        (branchScoreOf.get(b.attr) ?? 0) - (branchScoreOf.get(a.attr) ?? 0) ||
        (spineRank.get(a.attr) ?? 0) - (spineRank.get(b.attr) ?? 0),
    )[0];
    /* The plan-wide column stands unless the branch beats it decisively: a branch's
       ranking is measured over a fraction of the wall and its near-ties are noise. */
    const branchColumnMargin = 0.15;
    const planColumnUsable =
      planColumn && columnCandidates.includes(planColumn) ? planColumn : undefined;
    const measuredColumn =
      !planColumnUsable ||
      (branchScoreOf.get(branchBest?.attr ?? "") ?? 0) -
        (branchScoreOf.get(planColumnUsable.attr) ?? 0) >=
        branchColumnMargin
        ? branchBest
        : planColumnUsable;
    const column =
      (columnRuling && usable.includes(columnRuling) ? columnRuling : undefined) ??
      stated?.find((level) => columnCandidates.includes(level)) ??
      measuredColumn;
    const columnScore = branchScoreOf.get(column?.attr ?? "") ?? 0;
    if (column && columnScore < VERTICAL_SCORE) {
      warnings.push(
        `"${value}" has no columnar attribute to stand at cdt1 — "${column.attr}" is the best of them at ${columnScore.toFixed(2)}. This branch is banded rather than blocked, and its cdt1 windows will pool.`,
      );
    }

    /* A second columnar attribute compounds into cdt1 ("Tide : Pods"); it is never
       demoted to a banded tier. */
    const columnMates = usable.filter(
      (level) =>
        level !== column &&
        ruledHere(level.attr) !== "horizontal" &&
        ((branchScoreOf.get(level.attr) ?? 0) >= VERTICAL_SCORE ||
          ruledHere(level.attr) === "vertical"),
    );
    const columnLevels = column
      ? [column, ...columnMates].sort(
          (a, b) => (spineRank.get(a.attr) ?? 0) - (spineRank.get(b.attr) ?? 0),
        )
      : [];
    for (const mate of columnMates) {
      const label = `${value} / ${mate.attr}`;
      if (!demoted.includes(label)) demoted.push(label);
      warnings.push(
        `"${value}" stands up both "${column?.attr}" and "${mate.attr}", and there is one tier for them: cdt1 compounds the pair. Neither can be named on its own by a restriction, a window or a priority.`,
      );
    }

    /* cdt2: the attribute that really bands inside those columns. */
    let inner: readonly BlockingScope[] = scopes;
    for (const level of columnLevels) inner = splitBlockingScopes(inner, level.attr);
    const bandPool = usable.filter(
      (level) => !columnLevels.includes(level) && ruledHere(level.attr) !== "vertical",
    );
    const branchBand = bandPool.map((level) =>
      measureBandednessOf(measured, level.attr, runs, inner),
    );
    for (const reading of branchBand)
      allBandedness.push({ ...reading, attr: `${value} / ${reading.attr}` });
    const bandScoreOf = new Map(branchBand.map((reading) => [reading.attr, reading.score]));
    const bandRuling = asserted("cdt2");
    const ruledBand = bandRuling && bandPool.includes(bandRuling) ? bandRuling : undefined;
    const horizontalRulings = bandPool.filter((level) => ruledHere(level.attr) === "horizontal");
    const rankedBands = [...(horizontalRulings.length > 0 ? horizontalRulings : bandPool)].sort(
      (a, b) =>
        (bandScoreOf.get(b.attr) ?? 0) - (bandScoreOf.get(a.attr) ?? 0) ||
        (spineRank.get(a.attr) ?? 0) - (spineRank.get(b.attr) ?? 0),
    );
    const statedBand = stated?.find((level) => bandPool.includes(level));
    const bestBand = ruledBand ?? horizontalRulings[0] ?? statedBand ?? rankedBands[0];
    /* The singleton budget is a tie-break and a warning, never a veto: vetoing would
       hand the tier to whatever bands worst. */
    const selectedBand = ruledBand ?? horizontalRulings[0] ?? statedBand;
    const contenders = selectedBand
      ? [selectedBand]
      : bestBand
        ? rankedBands.filter(
            (level) =>
              (bandScoreOf.get(level.attr) ?? 0) >=
              (bandScoreOf.get(bestBand.attr) ?? 0) - bandTieMargin,
          )
        : [];
    const band =
      contenders.find((level) => singletonShare(measured, inner, level.attr) <= budget) ?? bestBand;
    const bandSingletons = band ? singletonShare(measured, inner, band.attr) : 0;
    if (band && bandSingletons > budget) {
      warnings.push(
        `"${value}" bands at cdt2 by "${band.attr}", ${(100 * bandSingletons).toFixed(0)}% of whose nodes hold one SKU — over the ${(100 * budget).toFixed(0)}% budget. It is carrying identity as much as grouping.`,
      );
    }

    /* cdt3: the branch's remaining attributes in spine order, compounded only while the
       tier still averages more than one SKU per node (past that it names products). */
    const bandScopes = band ? splitBlockingScopes(inner, band.attr) : inner;
    const candidates = (stated ?? usable)
      .filter(
        (level) => !columnLevels.includes(level) && level !== band && valuesIn(level.attr).size > 0,
      )
      .sort((a, b) => (stated ? 0 : (spineRank.get(a.attr) ?? 0) - (spineRank.get(b.attr) ?? 0)));
    const tail: SpineLevel[] = [];
    for (const level of candidates) {
      const occupancy = nodeOccupancy(
        bandScopes,
        [...tail, level].map((entry) => entry.attr),
      );
      // The first key always goes on: a sort tier with nothing in it sorts nothing.
      if (tail.length > 0 && occupancy < SORT_TIER_MIN_OCCUPANCY) break;
      tail.push(level);
    }

    noteUse(0, root.attr, weight);
    for (const level of columnLevels) noteUse(1, level.attr, weight);
    if (band) noteUse(2, band.attr, weight);
    for (const level of tail) noteUse(3, level.attr, weight);
    branches.push({
      value,
      skus: weight,
      tiers: [
        [root.attr],
        columnLevels.map((level) => level.attr),
        band ? [band.attr] : [],
        tail.map((level) => level.attr),
      ],
      because: [
        `cdt0 ${root.attr} names the branch`,
        columnLevels.length
          ? `cdt1 ${columnLevels.map((level) => level.attr).join("+")}, most columnar here (${columnScore.toFixed(2)})`
          : "cdt1 empty — nothing here splits",
        band
          ? `cdt2 ${band.attr}, bands most cleanly here (${(bandScoreOf.get(band.attr) ?? 0).toFixed(2)})`
          : "cdt2 empty — nothing left to band",
        tail.length
          ? `cdt3 ${tail.map((level) => level.attr).join("+")}, within-shelf sort`
          : "cdt3 empty",
      ],
    });
  }

  /* ---- 3. the plan-wide view: one entry per tier listing every attribute that lands
     there, heaviest branch first ---- */
  const byAttr = new Map(spine.map((level) => [level.attr, level]));
  const plan: PlannedLevel[] = [];
  for (let tier = 0; tier < SHIPPED_LEVELS; tier += 1) {
    const used = [...(usedAt.get(tier) ?? new Map<string, number>()).entries()].sort(
      (a, b) => b[1] - a[1] || (spineRank.get(a[0]) ?? 0) - (spineRank.get(b[0]) ?? 0),
    );
    const levels = used.flatMap(([attr]) => {
      const level = byAttr.get(attr);
      return level ? [level] : [];
    });
    if (levels.length === 0) continue;
    plan.push({
      levels,
      orient: orientOfTier(tier),
      role: tier < VERTICAL_TIERS ? "vertical" : "horizontal",
      because:
        tier === 0
          ? `coarsest measured-vertical attribute (${scoreOf(root.attr).toFixed(2)})`
          : tier === 1
            ? "most columnar attribute inside each branch"
            : tier === 2
              ? "bands most cleanly inside each branch's columns"
              : "within-shelf sort order",
    });
  }

  if (plan.length < SHIPPED_LEVELS) {
    warnings.push(
      `only ${plan.length} of ${SHIPPED_LEVELS} levels carry an attribute; the empty tiers ship blank and every relax factor addressed to them no-ops.`,
    );
  }

  /* Where the measured nesting differs from the working spine the tiers are cut across
     it, not along it, and the shipped tree will not reproduce the plan on screen. */
  const measuredOrder: string[] = [];
  for (const level of plan)
    for (const source of level.levels)
      if (!measuredOrder.includes(source.attr)) measuredOrder.push(source.attr);
  const spineOrder = spine
    .map((level) => level.attr)
    .filter((attr) => measuredOrder.includes(attr));
  const reordered =
    measuredOrder.length === spineOrder.length &&
    measuredOrder.every((attr, index) => attr === spineOrder[index])
      ? undefined
      : { before: spineOrder, after: measuredOrder };
  if (reordered) {
    warnings.push(
      `The base pog nests these attributes ${measuredOrder.join(" > ")}; the working plan nests them ${spineOrder.join(" > ")}. The shipped tree follows the wall, so it will not reproduce the plan on screen product for product.`,
    );
  }

  return {
    levels: plan,
    branches,
    reordered,
    verticality: allVerticality,
    bandedness: allBandedness,
    needsRuling,
    demoted,
    inert,
    warnings,
  };
};
